import { get, groupBy } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import DisplayNumber from '@commons/DisplayNumber';
import PictureWithActions from '@commons/PictureWithActions';

import {
  getIconActions,
  removeItemCell,
  renderDropdownCell,
  renderInputCell,
  renderNameCell,
} from '../utils/customCells';

export const getSupplierProductsColumns = ({
  lossCategories,
  currency,
  handleFieldUpdate,
  handleItemRemoval,
  supplierProductRows,
  canCreateOrUpdateLosses,
  canCorrectLosses,
  handleReplacePicture,
  handleDeletePicture,
  setShouldPictureFullScreen,
}) => {
  const canEdit = canCreateOrUpdateLosses && canCorrectLosses;

  const onReplacePicture = (lossId) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = (event) => handleReplacePicture(event, lossId);
    fileInput.click();
  };

  return [
    {
      field: 'name',
      headerName: i18next.t('GENERAL.NAME'),
      width: 400,
      sortable: true,
      renderCell: ({ row }) => renderNameCell(row),
    },
    {
      field: 'supplierName',
      headerName: i18next.t('GENERAL.SUPPLIER'),
      width: 230,
      sortable: true,
    },
    {
      field: 'packaging',
      headerName: i18next.t('GENERAL.PACKAGING'),
      width: 180,
      sortable: false,
      renderCell: ({ row }) => {
        const selectedPackaging = row.packagings.find(
          ({ id }) => id === row.supplierProductPackagingId,
        );

        const supplierProductRowGroupedBySPids = groupBy(supplierProductRows, 'supplierProductId');
        const associatedLosses = supplierProductRowGroupedBySPids[row.supplierProductId] || [];

        const allAssociatedLossesCompositeKeys = associatedLosses.reduce((acc, { key }) => {
          if (!!key) {
            acc.push(key);
          }

          return acc;
        }, []);

        const formattedPackagings = row.packagings.map((packaging) => {
          const compositeKeysForPackaging = allAssociatedLossesCompositeKeys.filter((key) =>
            key.includes(packaging.id),
          );

          if (compositeKeysForPackaging.length === lossCategories.length) {
            return {
              ...packaging,
              isDisabled: true,
            };
          }

          return packaging;
        });

        return renderDropdownCell(
          formattedPackagings,
          selectedPackaging,
          (item) => handleFieldUpdate('supplierProductPackagingId', item.id, row.id),
          !canEdit,
        );
      },
    },
    {
      field: 'quantity',
      headerName: i18next.t('GENERAL.QUANTITY'),
      width: 180,
      sortable: true,
      renderCell: ({ row }) =>
        renderInputCell(row, (value) => handleFieldUpdate('quantity', value, row.id), !canEdit),
    },
    {
      field: 'category',
      headerName: i18next.t('GENERAL.CATEGORY'),
      width: 180,
      sortable: false,
      renderCell: ({ row }) => {
        const selectedCategory = lossCategories.find(({ id }) => id === row.lossCategoryId);

        const supplierProductRowGroupedBySPids = groupBy(supplierProductRows, 'supplierProductId');
        const associatedLosses = supplierProductRowGroupedBySPids[row.supplierProductId] || [];

        const allAssociatedLossesCompositeKeys = new Set(associatedLosses.map(({ key }) => key));

        const formattedLossCategories = lossCategories.map((lossCategory) => ({
          ...lossCategory,
          isDisabled: allAssociatedLossesCompositeKeys.has(
            `${row.supplierProductPackagingId}-${lossCategory.id}`,
          ),
        }));

        return renderDropdownCell(
          formattedLossCategories,
          selectedCategory,
          (item) => handleFieldUpdate('lossCategoryId', item.id, row.id),
          !canEdit,
        );
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: ({ row }) => {
        const actions = getIconActions({
          seePicture: () => {
            setShouldPictureFullScreen({
              state: true,
              img: row.lossPictureUrl || get(row, 'lossPictureImage.data', null),
            });
          },
          replacePicture: () => onReplacePicture(row.id),
          deletePicture: () => handleDeletePicture(row.id),
        });

        return (
          <PictureWithActions
            actions={actions}
            disabled={!canEdit}
            image={row.lossPictureUrl || get(row, 'lossPictureImage.data', null)}
          />
        );
      },
    },
    {
      field: 'total',
      headerName: i18next.t('GENERAL.TOTAL_EX_TAX'),
      width: 120,
      sortable: true,
      renderCell: ({ row }) => {
        const priceByPackagingId = row.priceByPackagingId[row.supplierProductPackagingId];

        const total = priceByPackagingId * row.quantity;

        return <DisplayNumber currency={currency} number={total} displayCurrencyCode />;
      },
    },
    {
      field: 'close',
      headerName: '',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        if (!canEdit) {
          return null;
        }

        return removeItemCell(row, handleItemRemoval);
      },
    },
  ];
};
